import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { HorizontalFlex, icons, MoreMenu } from 'smartsuite-ui';
import WidgetActionsMenu from './widgets/widgetContextMenu/WidgetActionsMenu';
import { getLegendFormatter } from './widgets/utils/dataFormatUtils';
import { getChartRenderProps, hasFilterWarning } from './dashboardUtils';
import DashboardContext from '../shared/hooks/dashboardContext';
import { getThresholdsWithValues } from './utils/configurationAndValidationUtils';
import { buildWidgetMetricRequests } from './utils/metricRequestUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import TextOrLinkTitle from '../shared/TextOrLinkTitle';
import {
    buildWidgetFilterState,
    getNavigationLink,
} from './dashboardLinkUtils';
import { buildWidgetFilterWarning } from '../shared/filtering/filtersModalUtils';
import WidgetContextMenu from './widgets/widgetContextMenu/WidgetContextMenu';
import WidgetLoading from '../shared/WidgetLoading';

function getWidgetClass(hasChart = true) {
    let className = 'widget';

    if (hasChart) {
        className += ' widget--with-chart';
    }

    return className;
}

/* eslint-disable complexity */
function shouldShowWidgetFilter(
    metricRequests,
    showTimeAndPrecision,
    availableFilters
) {
    return (
        metricRequests &&
        metricRequests.length > 0 &&
        (showTimeAndPrecision || !_.isEmpty(availableFilters))
    );
}

function getWidgetStyles(widgetHeight, widgetWidth) {
    return {
        flexGrow: 1,
        width: widgetWidth,
        height: widgetHeight,
    };
}

export default function Widget(props) {
    const { widgetConfiguration } = props;
    const { uriConstants, thresholdValues } = useContext(DashboardContext);
    const { dashboardLabels, exportLabels, predicateLabels } =
        useLabelContext();

    const [uri, setUri] = useState(null);
    const [contextMenuData, setContextMenuData] = useState();
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [downloadData, setDownloadData] = useState();

    const widgetFilterState = buildWidgetFilterState(
        props.defaultTimeAndPrecision,
        props.defaultFilterValues,
        props.defaultWhereClauses,
        widgetConfiguration
    );

    useEffect(() => {
        let isUnmounted = false;

        async function getUri() {
            const navigationUri = await getNavigationLink(
                widgetConfiguration.navigationLink,
                uriConstants,
                [widgetFilterState],
                predicateLabels
            );

            if (!isUnmounted) {
                setUri(navigationUri);
            }
        }

        if (widgetConfiguration.navigationLink) {
            getUri();
        }

        return () => {
            isUnmounted = true;
        };
    }, [
        predicateLabels,
        uriConstants,
        widgetConfiguration.navigationLink,
        widgetFilterState,
    ]);

    function onContextMenuOpen(payload) {
        setContextMenuData(payload);
        setContextMenuOpen(true);
    }

    function onSelectorClick(rawDataForSelector) {
        props.onSelectorClick(
            widgetConfiguration.publisherFilters,
            rawDataForSelector
        );
    }

    function onSelectorRemoveClick() {
        props.onSelectorRemoveClick(widgetConfiguration.publisherFilters);
    }

    const legendFormatter = getLegendFormatter(
        widgetConfiguration.friendlyLegendLabels,
        props.friendlyColumnLabels
    );

    const showWidgetFilter = shouldShowWidgetFilter(
        widgetConfiguration.metricRequests,
        widgetConfiguration.showTimeAndPrecision,
        widgetConfiguration.availableFilters
    );

    const className = getWidgetClass(
        widgetConfiguration.configuration.hasChart
    );

    const thresholdsWithValues = getThresholdsWithValues(
        widgetConfiguration.thresholds,
        thresholdValues,
        widgetFilterState.filterValues
    );

    function renderWidgetBody() {
        if (props.isLoading) {
            return (
                <div
                    className="widget-body--loading"
                    style={widgetConfiguration.bodyStyles}
                >
                    <WidgetLoading noOverlayBackground={true} />
                </div>
            );
        }

        return (
            <div className="widget-body" style={widgetConfiguration.bodyStyles}>
                {widgetConfiguration.configuration.render(
                    getChartRenderProps(
                        widgetConfiguration,
                        buildWidgetMetricRequests(
                            widgetConfiguration,
                            props.defaultTimeAndPrecision,
                            props.defaultFilterValues,
                            props.defaultWhereClauses,
                            props.defaultRecordLimit,
                            false
                        ),
                        props.metrics,
                        thresholdsWithValues,
                        legendFormatter,
                        dashboardLabels,
                        props.openLinksInNewTab,
                        setDownloadData,
                        onContextMenuOpen,
                        onSelectorClick,
                        onSelectorRemoveClick
                    )
                )}
            </div>
        );
    }

    function renderDownloadMenu() {
        if (!downloadData) {
            return null;
        }

        const menuItems = [
            {
                description: `${exportLabels.downloadTable} (CSV)`,
                onClick: () =>
                    props.onDownload(downloadData, widgetConfiguration.title),
                sortOrder: 0,
                iconClass: icons.download,
            },
        ];

        return <MoreMenu menuItems={menuItems} />;
    }

    return (
        <div
            className={className}
            style={getWidgetStyles(props.widgetHeight, props.widgetWidth)}
        >
            {widgetConfiguration.title && (
                <div
                    className="widget__header"
                    style={widgetConfiguration.headerStyles}
                >
                    <div className="title title--size-small widget__header-text">
                        <TextOrLinkTitle
                            navigationLink={uri}
                            openLinkInNewTab={props.openLinksInNewTab}
                            title={widgetConfiguration.title}
                        />
                        {hasFilterWarning(
                            props.defaultFilterValues,
                            widgetConfiguration.inapplicableFilterKeys
                        ) && (
                            <div className="filter-warning-icon filter-warning-icon--header">
                                <span
                                    className={icons.warningFilled}
                                    title={buildWidgetFilterWarning(
                                        widgetConfiguration.inapplicableFilterKeys,
                                        props.availableFilters,
                                        dashboardLabels
                                    )}
                                />
                            </div>
                        )}
                    </div>
                    <HorizontalFlex>
                        {showWidgetFilter && (
                            <WidgetActionsMenu
                                {...widgetConfiguration}
                                {...widgetFilterState}
                                handleReset={props.handleReset(
                                    props.currentIndex
                                )}
                                handleSave={props.handleSave(
                                    props.currentIndex
                                )}
                                legendFormatter={legendFormatter}
                                precisionTypeOptions={
                                    props.precisionTypeOptions
                                }
                                predefinedDateRanges={
                                    props.predefinedDateRanges
                                }
                                timeAndPrecisionTypeOptions={
                                    props.timeAndPrecisionTypeOptions
                                }
                                timeAndPrecisionRangeDomains={
                                    props.timeAndPrecisionRangeDomains
                                }
                            />
                        )}
                    </HorizontalFlex>
                    {renderDownloadMenu()}
                </div>
            )}
            {renderWidgetBody()}
            {widgetConfiguration.actions && (
                <WidgetContextMenu
                    actions={widgetConfiguration.actions}
                    availableFilters={props.availableFilters}
                    contextMenuData={contextMenuData}
                    handleWidgetMenuFilter={props.handleWidgetMenuFilter}
                    isOpen={contextMenuOpen}
                    legendFormatter={legendFormatter}
                    onClose={() => setContextMenuOpen(false)}
                    openLinksInNewTab={props.openLinksInNewTab}
                />
            )}
        </div>
    );
}
/* eslint-enable complexity */

Widget.propTypes = {
    actions: PropTypes.instanceOf(Object),
    availableFilters: PropTypes.instanceOf(Object),
    currentIndex: PropTypes.number,
    defaultTimeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    defaultFilterValues: PropTypes.instanceOf(Object).isRequired,
    defaultWhereClauses: PropTypes.instanceOf(Array).isRequired,
    defaultRecordLimit: PropTypes.instanceOf(Object).isRequired,
    friendlyColumnLabels: PropTypes.instanceOf(Object).isRequired,
    handleReset: PropTypes.func,
    handleSave: PropTypes.func,
    isLoading: PropTypes.bool.isRequired,
    metrics: PropTypes.instanceOf(Array).isRequired,
    onDownload: PropTypes.func.isRequired,
    onSelectorClick: PropTypes.func,
    onSelectorRemoveClick: PropTypes.func,
    openLinksInNewTab: PropTypes.bool.isRequired,
    precisionTypeOptions: PropTypes.instanceOf(Object),
    predefinedDateRanges: PropTypes.instanceOf(Array).isRequired,
    handleWidgetMenuFilter: PropTypes.func,
    timeAndPrecisionTypeOptions: PropTypes.instanceOf(Object),
    timeAndPrecisionRangeDomains: PropTypes.instanceOf(Object),
    widgetConfiguration: PropTypes.instanceOf(Object).isRequired,
    widgetHeight: PropTypes.string.isRequired,
    widgetWidth: PropTypes.string.isRequired,
};

Widget.defaultProps = {
    handleReset: () => {},
    handleSave: () => {},
    onSelectorClick: () => {},
    onSelectorRemoveClick: () => {},
    handleWidgetMenuFilter: () => {},
};
